import { css } from "@emotion/react";
import * as React from "react";
import ReactTable, { TableProps } from "react-table";
import withFixedColumns from "react-table-hoc-fixed-columns";
import "react-table-hoc-fixed-columns/lib/styles.css";
import "react-table/react-table.css";

import { ArrowDownward, ArrowUpward, ArrowsSort } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, SpacingToken } from "@livechat/design-system-react-components";

import { withVar } from "themes/helpers";

import { breakdownTableStyles } from "./styled";

const ReactTableFixedColumns = withFixedColumns(ReactTable);

type ISorted = { id: string; desc: boolean };
export interface IBreakdownTableProps extends Partial<TableProps> {
  uniqClassName: string;
  isOpen?: boolean;
  truncateData?: boolean;
  sort?: ISorted;
}

export const BreakdownTable: React.FC<IBreakdownTableProps> = ({
  columns,
  data,
  uniqClassName,
  isOpen,
  truncateData = false,
  sort = { id: "total", desc: false },
}) => {
  const [sorted, setSorted] = React.useState<ISorted[]>([sort]);
  const [hoveredColumn, setHoveredColumn] = React.useState<string | null>(null);
  const [expanded, toggleExpanded] = React.useState(false);

  const handleToggle = React.useCallback(() => {
    toggleExpanded(!expanded);
  }, [expanded]);

  const handleSort = (sorted: ISorted[]): void => {
    setSorted(sorted);
  };

  const handleMouseEnter = (columnId: string): void => {
    setHoveredColumn(columnId);
  };

  const handleMouseLeave = (): void => {
    setHoveredColumn(null);
  };

  const tableColumns = columns?.map(column => ({
    ...column,
    Header: (): React.ReactElement => {
      const isSorted = sorted.find(sort => sort.id === column.accessor);
      const sortDirection = isSorted ? (isSorted.desc ? ArrowDownward : ArrowUpward) : ArrowsSort;
      const isHovered = hoveredColumn === column.accessor;

      return (
        <span
          css={headerStyles}
          onMouseEnter={() => handleMouseEnter(column.accessor as string)}
          onMouseLeave={handleMouseLeave}
        >
          {column.Header as React.ReactNode}
          {isHovered && !isSorted && (
            <Icon
              css={iconStyles}
              size="small"
              source={sortDirection}
              customColor={withVar(DesignToken.ContentBasicDisabled)}
            />
          )}
          {isSorted && <Icon css={iconStyles} size="small" source={sortDirection} />}
        </span>
      );
    },
  }));
  return (
    <>
      <div css={breakdownTableStyles(expanded)} className={isOpen ? "open" : ""}>
        <ReactTableFixedColumns
          columns={tableColumns}
          data={data}
          className={uniqClassName}
          showPagination={false}
          resizable={false}
          minRows={0}
          pageSize={data?.length}
          sorted={sorted}
          onSortedChange={handleSort}
        />
      </div>
      {truncateData && (
        <div css={buttonWrapperStyles}>
          <Button kind="link" onClick={handleToggle}>
            {expanded ? "Show less" : "Show more"}
          </Button>
        </div>
      )}
    </>
  );
};

const buttonWrapperStyles = css({
  borderTop: `1px solid ${withVar(DesignToken.BorderBasicPrimary)}`,
  textAlign: "center",
  paddingTop: withVar(SpacingToken.Spacing3),
});

const headerStyles = css({
  display: "flex",
});
const iconStyles = css({
  marginLeft: withVar(SpacingToken.Spacing2),
});
